// THE RING TIMELINE (docs SIGNOFF-SPEC S1, left; the style of the estimation app's
// timeline): one ring per event, alternating either side of a centre line, newest first.
// Gold a contractor sign-off, blue owner/GC, grey a model revision (with its own blue and
// gold stars), red a withdrawal. Scrolling
// drives the model: the ring nearest the top of the panel sets the 3D date; a click jumps.
import React, { useEffect, useMemo, useRef } from "react";
import { Input, Tag } from "antd";
import { CheckOutlined, ClockCircleOutlined, DownloadOutlined, EyeOutlined, SafetyCertificateOutlined, StarFilled } from "@ant-design/icons";
import moment from "moment";
import { RING, ordinal } from "./signoffState";
import { orderedParties } from "./SignoffCertificate";
import { dayOnly } from "./signoffTimes";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "ownerGc", label: "★ Owner/GC" },
  { key: "tenant", label: "★ Contractor" },
  { key: "partner", label: "★ Trade partner" },
  { key: "export", label: "Revisions" },
];

export const ringColor = (r) => (r.type === "export" ? RING.export : r.withdrawn ? RING.withdrawn : RING[r.level]);

export function headline(r) {
  if (r.type === "export") return `Revision #${r.number}${r.exportVersion ? ` · ${r.exportVersion}` : ""}`;
  const who = { tenant: "Contractor", partner: "Trade partner" }[r.level] || "Owner/GC";
  return `${r.withdrawn ? "Withdrawn" : `★ ${who}`} · ${(r.target && r.target.label) || ""}`;
}

// A revision's two stars: blue (owner/GC) and gold (the contractor), each given, pending
// on someone's link, or open to give / ask for.
const STARS = [["ownerGc", "Owner/GC"], ["tenant", "Contractor"]];
function RevisionStars({ r, rev }) {
  if (!rev) return null;
  return (
    <div className="ring-stars">
      {STARS.map(([star, word]) => {
        const s = (r.stars || {})[star];
        const pending = (r.pending || []).filter((p) => p.star === star);
        const mine = star === "ownerGc" ? rev.canBlue : rev.canGold;
        return (
          <div key={star} className={`ring-star ${s ? "is-signed" : ""}`} data-testid={`revision-${star}`}>
            <StarFilled style={{ color: s ? RING[star] : "#c7ccd4" }} /> <b>{word}</b>
            {s ? <> · {s.byName || s.byEmail}{s.byCompany ? ` (${s.byCompany})` : ""} · {moment(s.at).format("MMM D")}</> : null}
            {!s && pending.length ? <> · waiting on {pending.map((p) => p.name || p.email).join(", ")}</> : null}
            {!s && !pending.length ? <span className="signoff-muted"> · not signed</span> : null}
            <span className="ring-star-acts">
              {!s && mine ? <button type="button" onClick={() => rev.onStar(r, star, false)}>Sign off</button> : null}
              {s && (rev.mayWithdraw ? rev.mayWithdraw(s) : mine) ? <button type="button" onClick={() => rev.onStar(r, star, true)}>Withdraw</button> : null}
              {!s && !pending.length && rev.canRequest && !mine ? <button type="button" onClick={() => rev.onAsk(r, star)}>Ask</button> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Card({ r, side, onPick, onPdf, onChanges, rev, onCertificate }) {
  const color = ringColor(r);
  return (
    <div className={`ring-card ring-card-${side}`}>
      {r.type === "signoff" && onCertificate ? (
        <button type="button" className="ring-view" onClick={() => { onPick(r); onCertificate(r); }} data-testid="see-certificate"><SafetyCertificateOutlined /> See sign-off certificate</button>
      ) : (
        <button type="button" className="ring-view" onClick={() => onPick(r)}><EyeOutlined /> View model</button>
      )}
      <div className="ring-head" style={{ color, cursor: "pointer" }} onClick={() => onPick(r)} role="presentation">{headline(r)}</div>
      {r.type === "export" ? (
        <>
          <div className="ring-sub">+{r.added} / ~{r.modified} / −{r.removed} elements{r.live ? " · live" : ""} · view to see what changed</div>
          <RevisionStars r={r} rev={rev} />
        </>
      ) : (
        <>
          <div className="ring-sub">
            {r.withdrawn ? "sign-off withdrawn" : `${r.tileCount || 0} tile${r.tileCount === 1 ? "" : "s"} signed off`}
            {r.target && r.target.kind === "activity" ? " · schedule activity" : ""}
          </div>
          {/* the model it refers to and the dates (owner 2026-11-01) */}
          <div className="ring-dates">
            {r.model ? `Model rev #${r.model.revision}${r.model.exportedAt ? ` exported ${moment(r.model.exportedAt).format("MMM D")}` : ""} · ` : ""}
            Sign-off {r.agreedAt ? `${dayOnly(r.agreedAt)} (agreed)` : moment(r.at).format("MMM D, YYYY")}
            {r.signedAt && r.agreedAt ? ` · signed ${moment(r.signedAt).format("MMM D")}` : ""}
            {r.requiredBy ? ` · required by ${dayOnly(r.requiredBy)}` : ""}
          </div>
        </>
      )}
      {r.activities && r.activities.length ? (
        <div className="ring-acts" title={r.activities.join("\n")}>
          {r.activities.slice(0, 2).join(", ")}{r.activities.length > 2 ? ` +${r.activities.length - 2} more` : ""}
        </div>
      ) : null}
      {r.byName ? <div className="ring-by">by {r.byName}</div> : null}
      {orderedParties(r.parties).map((p, i) => (
        <div key={i} className={`ring-party ${p.signed ? "is-signed" : p.pending ? "is-pending" : "is-other"}`}>
          <StarFilled style={{ color: RING[p.star] || "#c7ccd4" }} /> {p.signed ? <CheckOutlined /> : <ClockCircleOutlined />} {p.name || p.email}
          {p.company ? ` · ${p.company}` : ""}{p.role ? ` · ${p.role}` : ""}{p.title ? ` · ${p.title}` : ""}
          {p.pending ? ` · pending${p.expiresAt ? `, link expires ${moment(p.expiresAt).format("MMM D")}` : ""}` : ""}
          {p.outcome === "changes" ? " · asked for changes" : ""}
        </div>
      ))}
      {r.type === "export" && onChanges ? (
        <button type="button" className="ring-file" onClick={() => onChanges(r)}><DownloadOutlined /> Changes.xlsx</button>
      ) : null}
      {r.type === "signoff" && onPdf ? (
        <button type="button" className="ring-file" onClick={() => onPdf(r)}><DownloadOutlined /> Sign-off certificate.pdf</button>
      ) : null}
    </div>
  );
}

export default function RingTimeline({ rings, activeId, onPick, onPdf, onChanges, onSpy, filter, onFilter, search, onSearch, height = 640, rev = null, onCertificate = null }) {
  const box = useRef(null);
  const els = useRef(new Map());
  const quiet = useRef(0);

  const shown = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return (rings || []).filter((r) => {
      if (filter === "export" && r.type !== "export") return false;
      if ((filter === "ownerGc" || filter === "tenant" || filter === "partner") && !(r.type === "signoff" && r.level === filter)) return false;
      if (!q) return true;
      const hay = [r.target && r.target.label, r.byName, ...(r.activities || []), ...(r.parties || []).map((p) => `${p.name} ${p.company}`), r.exportVersion].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rings, filter, search]);

  // scroll-spy: the ring nearest the top of the panel sets the date
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (Date.now() < quiet.current) return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const top = el.getBoundingClientRect().top;
        let best = null; let bestD = Infinity;
        for (const r of shown) {
          const node = els.current.get(r.id);
          if (!node) continue;
          const d = Math.abs(node.getBoundingClientRect().top - top - 8);
          if (d < bestD) { bestD = d; best = r; }
        }
        if (best && best.id !== activeId) onSpy(best);
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [shown, activeId, onSpy]);

  // a ring picked elsewhere (the scrubber) is scrolled into view without the spy fighting it
  useEffect(() => {
    const node = activeId && els.current.get(activeId);
    const el = box.current;
    if (!node || !el) return;
    const top = node.offsetTop - el.offsetTop;
    if (Math.abs(el.scrollTop - top) > 40) {
      quiet.current = Date.now() + 600;
      el.scrollTo({ top, behavior: "smooth" });
    }
  }, [activeId]);

  return (
    <div className="signoff-rings-panel">
      <div className="signoff-rings-head">
        <div className="signoff-panel-title">Timeline</div>
        <Input allowClear placeholder="Search areas, people…" value={search} onChange={(e) => onSearch(e.target.value)} />
        <div className="signoff-chips">
          {FILTERS.map((f) => (
            <Tag.CheckableTag key={f.key} checked={filter === f.key} onChange={() => onFilter(f.key)}>{f.label}</Tag.CheckableTag>
          ))}
        </div>
        <small className="signoff-muted">Scroll to replay · click a ring to jump</small>
      </div>
      <div className="signoff-rings" ref={box} style={{ maxHeight: height }} data-testid="signoff-rings">
        {!shown.length ? <p className="signoff-muted p-3">Nothing on the timeline yet.</p> : null}
        {shown.map((r, i) => {
          const side = i % 2 === 0 ? "left" : "right";
          const m = moment(r.at);
          const color = ringColor(r);
          return (
            <div key={r.id} ref={(n) => { if (n) els.current.set(r.id, n); else els.current.delete(r.id); }}
                 className={`ring-row ${activeId === r.id ? "is-active" : ""}`}>
              <div className="ring-col">{side === "left" ? <Card r={r} side="left" onPick={onPick} onPdf={onPdf} onChanges={onChanges} rev={rev} onCertificate={onCertificate} /> : null}</div>
              <div className="ring-mid">
                <span className="ring-link" style={{ background: color, [side === "left" ? "left" : "right"]: 0 }} />
                <button type="button" className="ring" style={{ borderColor: color }} onClick={() => onPick(r)} aria-label={headline(r)}>
                  <b>{ordinal(m.date())}</b>
                  <b>{m.format("MMMM")}</b>
                  <span>{m.format("h:mm a")}</span>
                  <span>{m.format("YYYY")}</span>
                </button>
              </div>
              <div className="ring-col">{side === "right" ? <Card r={r} side="right" onPick={onPick} onPdf={onPdf} onChanges={onChanges} rev={rev} onCertificate={onCertificate} /> : null}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
